import { useNavigation, useRoute } from "@react-navigation/native";
import { AHText, Box, TouchableBox, useTheme, Image, AHIcon } from "arkh";

import React, { useCallback } from "react";
import { StyleSheet } from "react-native";
import { graphql, useFragment } from "react-relay";
import { PLUListItemSpacer } from "../PLUListItemSpacer";
import { PLUProductItem_PLUProduct$key } from "./__generated__/PLUProductItem_PLUProduct.graphql";
import { AHBarcode } from "../AHBarcode";
import { PLUProductEditOverlay } from "./PLUProductEditOverlay";
import { usePLUProductItemLayout } from "./PLUProductItemLayout";
import { PLUViewMode } from "../PLUViewMode";

import { PLUProductItemFooter_PLUProduct$key } from "./__generated__/PLUProductItemFooter_PLUProduct.graphql";
import { PLUProductItemBody_PLUProduct$key } from "./__generated__/PLUProductItemBody_PLUProduct.graphql";

import { PLUProductItemCategoryTag_PLUCategory$key } from "./__generated__/PLUProductItemCategoryTag_PLUCategory.graphql";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import Animated, {
  SharedValue,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";

import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { ErrorBoundary } from "react-error-boundary";

export const PLUProductItem = React.memo(InnerPLUProductItem);

const PLUItemFooter = React.memo(InnerPLUItemFooter);
const PLUItemBody = React.memo(InnerPLUItemBody);
function InnerPLUProductItem(props: {
  pluProduct: PLUProductItem_PLUProduct$key;
  itemWidth: number;
  focusedFocusId: SharedValue<string | null>;
  onFocus: (id: string) => void;
}) {
  const data = useFragment(
    graphql`
      fragment PLUProductItem_PLUProduct on PLUProduct {
        id
        ...PLUProductItemFooter_PLUProduct
        ...PLUProductItemBody_PLUProduct
      }
    `,
    props.pluProduct
  );

  const {
    params: { isEditing, searching, mode },
  } = useRoute<UserLoggedInNavigatorRouteProp<"TeamPLU">>();
  const pluProductItemLayout = usePLUProductItemLayout();
  const navigation =
    useNavigation<UserLoggedInNavigatorNavigationProp<"TeamPLU">>();

  const onFocus = props.onFocus;
  const onPress = useCallback(() => {
    if (isEditing) {
      navigation.navigateDeprecated("PLUItemEdit", {
        id: data.id,
      });
    } else {
      onFocus(data.id);
    }
  }, [data.id, isEditing, navigation, onFocus]);

  const footerStyle = useAnimatedStyle(() => {
    const hideFooter =
      props.focusedFocusId.value !== null &&
      mode === "selfCheckoutCheck" &&
      props.focusedFocusId.value !== data.id;
    return {
      opacity: withTiming(hideFooter ? 0 : 1, {
        duration: 100,
      }),
    };
  });
  return (
    <PLUListItemSpacer itemWidth={props.itemWidth}>
      <TouchableBox
        style={{
          overflow: "hidden",
          height: pluProductItemLayout.height,
          borderWidth: 1,
        }}
        cornerRadius="medium"
        borderColor="border-primary"
        disabled={!isEditing && mode === "checkout"}
        onPress={onPress}
      >
        <Box
          color="surface"
          space={pluProductItemLayout.space}
          style={{
            flex: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PLUItemBody
            itemWidth={props.itemWidth}
            product={data}
            showCategory={searching}
          />
          <Animated.View style={footerStyle}>
            <PLUItemFooter
              itemWidth={props.itemWidth}
              mode={mode}
              product={data}
            />
          </Animated.View>
        </Box>

        {isEditing ? <PLUProductEditOverlay /> : null}
      </TouchableBox>
    </PLUListItemSpacer>
  );
}

function InnerPLUItemBody(props: {
  itemWidth: number;
  showCategory: boolean;
  product: PLUProductItemBody_PLUProduct$key;
}) {
  const { id, title, active, category, imageUrl, image } = useFragment(
    graphql`
      fragment PLUProductItemBody_PLUProduct on PLUProduct {
        id
        title
        active
        category {
          ...PLUProductItemCategoryTag_PLUCategory
        }
        imageUrl
        image {
          id
          sizes {
            width
          }
          ...Image_image
        }
      }
    `,
    props.product
  );

  const theme = useTheme();

  return (
    <>
      <Box
        style={{
          ...StyleSheet.absoluteFillObject,
        }}
      >
        {image ? (
          <Image
            cachePolicy="memory-disk"
            style={{
              flex: 1,
              width: "100%",
              height: "100%",
            }}
            image={image}
            type="remote"
            contentFit={
              (Math.max(...image.sizes.map((s) => s.width)) ?? 0) <= 300
                ? "scale-down"
                : "cover"
            }
          />
        ) : null}

        {!image && imageUrl ? (
          <>
            <Image
              recyclingKey={id}
              cachePolicy="memory-disk"
              type="local"
              uri={imageUrl}
              style={{
                flex: 1,
                width: "100%",
                height: "100%",
              }}
              contentFit="cover"
            />
          </>
        ) : null}
      </Box>
      <Box
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          alignSelf: "stretch",
        }}
      >
        <AHText
          style={{ backgroundColor: theme.surface }}
          spaceVertical="xxsmall"
          spaceHorizontal="xsmall"
          cornerRadius="small"
          type="title"
          color="darkerText"
          numberOfLines={2}
          spaceRight="small"
        >
          {title}
        </AHText>
        {props.showCategory || !active ? (
          <Box
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: -28,
              zIndex: 100,
              alignSelf: "flex-start",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {props.showCategory ? (
              <PLUProductItemCategoryTag category={category} />
            ) : null}
            {props.showCategory && !active ? <Box spaceRight="xsmall" /> : null}
            {active ? null : <PLUProductItemHiddenTag />}
          </Box>
        ) : null}
      </Box>
    </>
  );
}

const PLUProductItemHiddenTag = () => {
  const intl_tokens = useIntlTokens();
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
      }}
      cornerRadius="small"
      color="surface"
      space="xsmall"
      spaceHorizontal="small"
    >
      <AHText type="subtitle">
        <AHIcon type="hide" forceSize={12} /> {intl_tokens.Hidden}
      </AHText>
    </Box>
  );
};

const PLUProductItemCategoryTag = (props: {
  category: PLUProductItemCategoryTag_PLUCategory$key;
}) => {
  const category = useFragment(
    graphql`
      fragment PLUProductItemCategoryTag_PLUCategory on PLUCategory {
        id
        color
        title
      }
    `,
    props.category
  );

  return (
    <Box
      cornerRadius="small"
      space="xsmall"
      spaceHorizontal="small"
      style={{
        backgroundColor: category.color,
        flexDirection: "row",
      }}
    >
      <AHText color="lightText" type="subtitle" numberOfLines={1}>
        {category.title}
      </AHText>
    </Box>
  );
};

function InnerPLUItemFooter(props: {
  product: PLUProductItemFooter_PLUProduct$key;
  mode: PLUViewMode;
  itemWidth: number;
}) {
  const { pluCode, eanCode, eanType } = useFragment(
    graphql`
      fragment PLUProductItemFooter_PLUProduct on PLUProduct {
        pluCode
        eanCode
        eanType
      }
    `,
    props.product
  );
  const pluProductItemLayout = usePLUProductItemLayout();

  return props.mode === "selfCheckoutCheck" ? (
    eanCode && eanCode !== "" ? (
      <ErrorBoundary FallbackComponent={() => null}>
        <Box
          color="surface"
          cornerRadius="small"
          space="small"
          style={{
            flexDirection: "row",
          }}
        >
          <AHBarcode
            value={eanCode}
            height={pluProductItemLayout.barCodeHeight}
            containerWidth={
              eanCode.length === 13
                ? props.itemWidth - pluProductItemLayout.padding * 4
                : props.itemWidth - pluProductItemLayout.padding * 8
            }
            format={
              eanType === "CODE39" || eanType === "CODE128"
                ? eanType
                : eanCode.length === 13
                  ? "EAN13"
                  : "EAN8"
            }
          />
        </Box>
      </ErrorBoundary>
    ) : (
      <Box style={{ height: pluProductItemLayout.barCodeHeight }} />
    )
  ) : pluCode ? (
    <Box
      color="surface"
      cornerRadius="small"
      style={{
        flexDirection: "row",
        alignSelf: "flex-start",
      }}
    >
      <AHText type="title-big" spaceVertical="xxsmall" spaceHorizontal="xsmall">
        {pluCode}
      </AHText>
    </Box>
  ) : null;
}
